import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { catLabel, SWATCHES } from "@/lib/lore";
import { StageList } from "@/components/RegionTime";
import { Hexagon } from "lucide-react";

export const RegionDialog = ({ open, onOpenChange, mapId, region, entities, onSaved, timelines = [], time, resolved, onEditShape }) => {
  const [form, setForm] = useState({ name: region.name || "", color: region.color || "#D97706", opacity: region.opacity ?? 0.3, entity_id: region.entity_id || "none", start_t: region.start_t ?? "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...form, entity_id: form.entity_id === "none" ? null : form.entity_id, start_t: form.start_t === "" ? null : parseInt(form.start_t, 10) };
    try {
      if (region.id) await api.patch(`/regions/${region.id}`, body);
      else await api.post(`/maps/${mapId}/regions`, { ...body, points: region.points });
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    await api.delete(`/regions/${region.id}`);
    onSaved();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto lf-scroll" data-testid="region-dialog">
        <form onSubmit={save}>
          <DialogHeader><DialogTitle className="font-display text-3xl">{region.id ? "Edit region" : "Name this region"}</DialogTitle><DialogDescription className="sr-only">Region name, colour, owner and changes over time</DialogDescription></DialogHeader>
          {region.id && (
            <Button type="button" variant="outline" size="sm" className="mt-4" onClick={() => { onOpenChange(false); onEditShape(region); }} data-testid="region-edit-shape-button">
              <Hexagon className="mr-1.5 h-4 w-4" />{time ? `Edit shape at year ${time.t}` : "Edit shape"}
            </Button>
          )}
          <div className="space-y-4 py-6">
            <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={set("name")} required placeholder="Kingdom of Varn" data-testid="region-name-input" /></div>
            <div className="space-y-2">
              <Label>Colour</Label>
              <div className="flex flex-wrap gap-1.5">
                {SWATCHES.map((c) => (
                  <button type="button" key={c} onClick={() => set("color")(c)} data-testid={`region-color-${c}`}
                    className={`h-6 w-6 rounded-full ${form.color === c ? "ring-2 ring-offset-2 ring-offset-[#13171F] ring-[#F3EBDD]" : ""}`} style={{ background: c }} />
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Opacity · {Math.round(form.opacity * 100)}%</Label>
              <Slider min={5} max={90} step={5} value={[Math.round(form.opacity * 100)]} onValueChange={([v]) => set("opacity")(v / 100)} data-testid="region-opacity-slider" />
            </div>
            <div className="space-y-2">
              <Label>Linked lore entry</Label>
              <Select value={form.entity_id} onValueChange={set("entity_id")}>
                <SelectTrigger data-testid="region-entity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No entry —</SelectItem>
                  {entities.map((x) => <SelectItem key={x.id} value={x.id}>{x.name} · {catLabel(x.category)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Exists from year <span className="text-[#A8A090] font-normal">(blank = always)</span></Label>
              <Input type="number" className="w-32 font-mono-lf" value={form.start_t} onChange={set("start_t")} placeholder="—" data-testid="region-start-input" />
            </div>
            {region.id && (
              <div className="border-t border-[#2D3442] pt-4">
                <p className="mb-3 text-xs text-[#A8A090]">The settings above are the region's starting state.</p>
                <StageList region={region} timelines={timelines} entities={entities} swatches={SWATCHES} time={time} resolved={resolved || region} api={api} onSaved={onSaved} />
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            {region.id && <Button type="button" variant="ghost" className="text-red-400 mr-auto" onClick={remove} data-testid="region-delete-button">Delete region</Button>}
            <Button type="submit" disabled={busy} data-testid="region-save-button">{busy ? "Saving…" : "Save region"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
